import { type HealthResponse, healthResponseSchema } from '@hrms/shared'
import { useQuery } from '@tanstack/react-query'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Machine-readable `error` from the JSON body, when there is one. */
    readonly code?: string,
    /** The parsed JSON error body (e.g. per-field errors). */
    readonly data?: unknown,
  ) {
    super(message)
  }
}

let onUnauthorized: (() => void) | null = null

/** The AuthProvider registers this: any 401 from the API signs the user out locally. */
export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler
}

interface ErrorBody {
  error?: unknown
  message?: unknown
}

async function request(path: string, init: RequestInit): Promise<{ res: Response; data: unknown }> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: { Accept: 'application/json', ...init.headers },
    credentials: 'same-origin',
  })
  const data: unknown = await res.json().catch(() => null)
  if (res.status === 401 && path !== '/me') onUnauthorized?.()
  return { res, data }
}

function toError(res: Response, data: unknown): ApiError {
  const body = (data ?? {}) as ErrorBody
  return new ApiError(
    res.status,
    typeof body.message === 'string' ? body.message : res.statusText || 'Request failed',
    typeof body.error === 'string' ? body.error : undefined,
    data ?? undefined,
  )
}

export async function apiGet<T>(path: string, parse: (data: unknown) => T): Promise<T> {
  const { res, data } = await request(path, { method: 'GET' })
  // The health endpoint returns a valid body with 503 when the DB is down.
  if (!res.ok && (data === null || res.status !== 503)) throw toError(res, data)
  return parse(data)
}

/** POST / PATCH / DELETE with a JSON body. Throws ApiError on non-2xx. */
export async function apiSend<T>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  parse: (data: unknown) => T = (d) => d as T,
): Promise<T> {
  const { res, data } = await request(path, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!res.ok) throw toError(res, data)
  return parse(data)
}

export function useHealth() {
  return useQuery<HealthResponse>({
    queryKey: ['health'],
    queryFn: () => apiGet('/health', (d) => healthResponseSchema.parse(d)),
    refetchInterval: 30_000,
    retry: false,
  })
}
